import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  useTheme
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import InputField from 'src/common/formFields/InputField';
import SelectField from 'src/common/formFields/SelectField';
import Loader from 'src/common/Loader';
import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import { addSiteManager, updateSiteManager } from 'src/redux/siteManagerSlice';
import { getAllSites } from 'src/redux/siteSlice';
import {
  getSiteManagerValidationSchema,
  siteManagerFormInitialValues,
  siteManagerFormModel
} from './siteManagerFormModel';

const NewSiteManager = ({ open, handleClose, initialValues }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { allSites } = useSelector((state) => state.sites);

  const [loading, setLoading] = useState(false);
  const formInitValues = initialValues || siteManagerFormInitialValues;

  const {
    formId,
    formField: {
      siteId,
      empId,
      firstName,
      lastName,
      username,
      password,
      confirmPassword,
      email,
      mobileNum,
      addressLine,
      city,
      state,
      pinCode,
      country
    }
  } = siteManagerFormModel;

  useEffect(() => {
    if (open) {
      dispatch(getAllSites());
    }
  }, [open, dispatch]);

  const onSubmit = async (values, actions) => {
    setLoading(true);
    try {
      if (initialValues) {
        await dispatch(updateSiteManager(values)).unwrap();
      } else {
        await dispatch(addSiteManager(values)).unwrap();
      }

      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Site Supervisor updated successfully'
            : 'New Site Supervisor created successfully'
        })
      );

      handleClose();
    } catch (error) {
      actions.setSubmitting(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    }

    setLoading(false);
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <FMSModalTitle
        title={initialValues ? 'UPDATE SITE SUPERVISOR' : 'ADD NEW SITE SUPERVISOR'}
        onCancel={handleClose}
      />

      <Divider />

      <Formik
        initialValues={formInitValues}
        validationSchema={getSiteManagerValidationSchema(initialValues)}
        onSubmit={onSubmit}
      >
        {({ isSubmitting }) => (
          <>
            {loading ? (
              <Loader />
            ) : (
              <Form
                id={formId}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '80vh'
                }}
              >
                <DialogContent
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 3
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={siteId.name}
                        label={siteId.label}
                        data={allSites}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={empId.name}
                        label={empId.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={firstName.name}
                        label={firstName.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={lastName.name}
                        label={lastName.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={username.name}
                        label={username.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={email.name}
                        label={email.label}
                        fullWidth
                      />
                    </Grid>

                    {!initialValues && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <InputField
                            name={password.name}
                            label={password.label}
                            type="password"
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <InputField
                            name={confirmPassword.name}
                            label={confirmPassword.label}
                            type="password"
                            fullWidth
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={mobileNum.name}
                        label={mobileNum.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={addressLine.name}
                        label={addressLine.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={city.name}
                        label={city.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={pinCode.name}
                        label={pinCode.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={state.name}
                        label={state.label}
                        data={state.data}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={country.name}
                        label={country.label}
                        data={country.data}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </DialogContent>

                <Divider />

                <DialogActions
                  sx={{
                    p: 2.5,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    bgcolor: '#F9FAFB'
                  }}
                >
                  <Button
                    onClick={handleClose}
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3
                    }}
                  >
                    {initialValues ? 'Update Supervisor' : 'Create Supervisor'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </>
        )}
      </Formik>
    </FMSModal>
  );
};

NewSiteManager.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewSiteManager;
